import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const PartnerLogos = () => {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cms/partner-logos`);
      setLogos(res.data);
    } catch {
      toast.error("Failed to load logos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (idx, patch) => {
    const next = [...logos];
    next[idx] = { ...next[idx], ...patch };
    setLogos(next);
  };

  const move = (idx, dir) => {
    const next = [...logos];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setLogos(next.map((l, i) => ({ ...l, order: i })));
  };

  const addNew = async () => {
    try {
      const res = await axios.post(`${API}/cms/partner-logos`, {
        id: crypto.randomUUID(),
        name: "New Partner",
        logo_url: "",
        website: "",
        order: logos.length,
        is_visible: true,
      });
      setLogos([...logos, res.data]);
    } catch {
      toast.error("Could not add logo");
    }
  };

  const saveOne = async (logo) => {
    try {
      await axios.put(`${API}/cms/partner-logos/${logo.id}`, logo);
      toast.success(`${logo.name} saved`);
    } catch {
      toast.error("Save failed");
    }
  };

  const saveAll = async () => {
    try {
      await Promise.all(logos.map((l) => axios.put(`${API}/cms/partner-logos/${l.id}`, l)));
      toast.success("All logos saved");
    } catch {
      toast.error("Some logos failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this logo?")) return;
    await axios.delete(`${API}/cms/partner-logos/${id}`);
    setLogos(logos.filter((l) => l.id !== id));
    toast.success("Removed");
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Partner Logos</h1>
          <p className="text-sm text-slate-500">Logos displayed in the "Our Partners" section on the homepage.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={addNew} className="inline-flex items-center gap-2 bg-prepify-600 hover:bg-prepify-700 text-white font-medium px-4 py-2.5 rounded-lg" data-testid="add-logo-btn">
            <Plus size={16} /> Add logo
          </button>
          <button onClick={saveAll} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-medium px-4 py-2.5 rounded-lg" data-testid="save-all-logos-btn">
            <Save size={16} /> Save all
          </button>
        </div>
      </header>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : logos.length === 0 ? (
        <div className="text-slate-400 text-center py-16 border-2 border-dashed border-slate-200 rounded-xl">
          No partner logos yet. Click <b>Add logo</b> to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {logos.map((l, idx) => (
            <div key={l.id} className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid={`logo-${l.id}`}>
              <div className="flex items-center gap-2">
                <div className="w-20 h-20 bg-slate-50 rounded-lg border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                  {l.logo_url ? <img src={l.logo_url.startsWith("/") ? `${BASE}${l.logo_url}` : l.logo_url} alt={l.name} className="max-w-full max-h-full object-contain" /> : <span className="text-xs text-slate-400">No logo</span>}
                </div>
                <div className="flex-1 space-y-2">
                  <input value={l.name} onChange={(e) => update(idx, { name: e.target.value })} placeholder="Partner name" className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm" />
                  <input value={l.logo_url || ""} onChange={(e) => update(idx, { logo_url: e.target.value })} placeholder="Logo URL (from Media Library)" className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm" />
                  <input value={l.website || ""} onChange={(e) => update(idx, { website: e.target.value })} placeholder="Website (optional)" className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm" />
                </div>
              </div>
              <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                <div className="flex items-center gap-2">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="px-2 py-0.5 text-xs border border-slate-200 rounded disabled:opacity-30">↑</button>
                  <button onClick={() => move(idx, 1)} disabled={idx === logos.length - 1} className="px-2 py-0.5 text-xs border border-slate-200 rounded disabled:opacity-30">↓</button>
                  <button onClick={() => update(idx, { is_visible: !l.is_visible })} className={`text-sm inline-flex items-center gap-1 ${l.is_visible ? "text-prepify-600" : "text-slate-400"}`}>
                    {l.is_visible ? <Eye size={14} /> : <EyeOff size={14} />} {l.is_visible ? "Visible" : "Hidden"}
                  </button>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => saveOne(l)} className="text-xs bg-slate-900 text-white rounded px-3 py-1 hover:bg-prepify-600">Save</button>
                  <button onClick={() => remove(l.id)} className="text-xs text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PartnerLogos;
